import { useState } from 'react';
import { Button, EmptyState, Icon, Panel, useErrorDialog } from '@dayon/design-system';
import { IDENTITY_IMAGE_LABEL } from '@dayon/contracts';
import { api } from '../../lib/api.ts';

/**
 * From `@dayon/contracts`, which is where the player's own form gets them too.
 *
 * This said `SELFIE: 'Selfie'`, and item 13 asks for a selfie HOLDING THE ID — the
 * distinction that is the entire reason the photograph is collected, since the service
 * face-matches against its own live capture and never looks at this one. A reviewer
 * reading "Selfie" sees a face and ticks it. The first real case through this screen was
 * a passport-style headshot with no document in frame.
 */
const IMAGE_LABEL: Record<string, string> = IDENTITY_IMAGE_LABEL;

/**
 * THE PHOTOGRAPHS OF ONE IDENTITY CHECK, and the only way any screen opens them.
 *
 * Moved out of the case drawer on Identity checks so the player's KYC tab could show a decided
 * check's photographs through the SAME door — `GET /api/kyc/identity/:id/images/:kind`, which
 * writes `player.identity_document_access` before the signed link exists. A second path to these
 * images would be a second place a look could go unrecorded, so there is none.
 *
 * Mount it with `key={recordId}`: a signed link belongs to one record, and one left on screen
 * would show the previous player's document under the next player's name. It is a 60-second
 * URL, so the window for that is small and the consequence is not.
 */
export function IdentityPhotos({
  recordId,
  playerRef,
  imagesHeld,
  empty,
}: {
  recordId: string;
  playerRef: string;
  imagesHeld: readonly string[];
  /** What to say when no photograph is held — which depends on why. */
  empty: string;
}) {
  const { showUnexpected } = useErrorDialog();
  /** The photographs revealed on this record, by kind. */
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  /**
   * Reveal a photograph in the panel.
   *
   * ── ONE PRESS IS STILL ONE RECORDED ACCESS ────────────────────────────────
   *
   * Every signed link writes a row to `player.identity_document_access` — who opened
   * which photograph, and when. That is why these are not loaded automatically when a
   * case opens: a reviewer who glanced at a queue would otherwise be recorded as having
   * looked at three people's passports. The press is the deliberate act the row claims
   * it was.
   *
   * So the link is minted once and then BOTH shows the image here and backs the
   * open-in-a-new-tab. Opening the tab reuses the URL already minted rather than asking
   * for another, because the reviewer looking at the bigger version of a photograph they
   * are already looking at is one access, not two.
   */
  const reveal = async (kind: string) => {
    try {
      const link = await api.get<{ url: string; expiresAt: string }>(
        `/api/kyc/identity/${encodeURIComponent(recordId)}/images/${encodeURIComponent(kind)}`,
      );
      setRevealed((held) => ({ ...held, [kind]: link.url }));
    } catch (error) {
      showUnexpected(error, 'Could not open that photograph');
    }
  };

  return (
    <Panel title="Photographs">
      <p className="text-subtle" style={{ marginTop: 0 }}>
        Each is fetched when you press Show, lasts one minute, and is recorded against your
        account — who opened which photograph, and when. Click a photograph to open it full
        size in a new tab; if the minute has passed, press Show again.
      </p>
      {imagesHeld.length === 0 ? (
        <EmptyState icon="image" title="No photographs held">
          {empty}
        </EmptyState>
      ) : (
        <div
          style={{
            display: 'grid',
            /* `auto-fill` rather than a fixed count: three fit on a wide drawer and
               one fits on a phone, without a breakpoint to maintain. */
            gridTemplateColumns: 'repeat(auto-fill, minmax(13rem, 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {imagesHeld.map((kind) => {
            const url = revealed[kind];
            const label = IMAGE_LABEL[kind] ?? kind;
            return (
              <div key={kind} style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <span className="text-muted">{label}</span>
                {url === undefined ? (
                  /* NOT LOADED UNTIL ASKED. Every signed link writes an access row,
                     so a reviewer merely opening a case must not be recorded as
                     having looked at somebody's passport. */
                  <Button variant="secondary" aria-label={`Show ${label}`} onClick={() => void reveal(kind)}>
                    <Icon name="eye" /> Show
                  </Button>
                ) : (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    /* The same URL already minted — opening the bigger version of a
                       photograph you are already looking at is one access, not two. */
                    title="Open full size in a new tab"
                    style={{
                      display: 'block',
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                      border: '1px solid var(--color-divider)',
                    }}
                  >
                    <img
                      src={url}
                      alt={`${label} for ${playerRef}`}
                      style={{
                        display: 'block',
                        width: '100%',
                        /* CONTAIN, not cover. A cropped ID hides exactly the corner a
                           reviewer is checking for, and item 13 asks for all four. */
                        objectFit: 'contain',
                        maxHeight: '14rem',
                        background: 'var(--color-surface)',
                      }}
                    />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
